import type React from 'react';
import { cn } from '../../lib/utils';

export interface PageLayoutProps {
  children: React.ReactNode;
  /** Page title displayed in the editorial header */
  title: string;
  /** Optional description shown below the title */
  description?: string;
  /** Optional action buttons displayed in the header */
  actions?: React.ReactNode;
  /** Layout variant: 'default' for standard pages, 'sidebar' for pages using SidebarProvider */
  variant?: 'default' | 'sidebar';
  /** Content max width: 'full' for no constraint, '6xl' for max-w-6xl */
  maxWidth?: 'full' | '6xl';
  /** Additional className for the content container */
  contentClassName?: string;
}

/**
 * Editorial Header Component
 *
 * Renders the branded header with decorative elements:
 * - Ochre horizontal line
 * - Burnt sienna dot accent
 * - Display font title
 * - Optional description and action buttons
 */
export const EditorialHeader: React.FC<{
  title: string;
  description?: string;
  actions?: React.ReactNode;
}> = ({ title, description, actions }) => (
  <div className="border-b border-ochre/10 bg-paper-warm/30">
    <div className="flex items-center justify-between px-6 py-3">
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-px w-5 bg-ochre" />
          <div className="w-1 h-1 rounded-full bg-burnt-sienna" />
        </div>
        <h1
          className="text-responsive-5xl font-bold text-charcoal tracking-tight"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {title}
        </h1>
        {description && (
          <p className="text-muted-warm mt-0.5 text-xs">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  </div>
);

/**
 * PageLayout Component
 *
 * Provides consistent page structure with:
 * - Editorial header with branded styling
 * - Warm paper-light background
 * - Configurable content width
 * - Support for sidebar layouts
 *
 * @example
 * // Standard page
 * <PageLayout title="Dashboard" description="Welcome back">
 *   <YourContent />
 * </PageLayout>
 *
 * @example
 * // Page with actions
 * <PageLayout
 *   title="Articles"
 *   actions={<Button>Create New</Button>}
 * >
 *   <ArticleList />
 * </PageLayout>
 *
 * @example
 * // Sidebar variant (header only, for use inside SidebarInset)
 * <SidebarProvider>
 *   <Sidebar>...</Sidebar>
 *   <SidebarInset>
 *     <PageLayout variant="sidebar" title="Insights">
 *       <Content />
 *     </PageLayout>
 *   </SidebarInset>
 * </SidebarProvider>
 */
export const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  title,
  description,
  actions,
  variant = 'default',
  maxWidth = '6xl',
  contentClassName,
}) => {
  // For sidebar variant, only render the header and content without outer wrapper
  if (variant === 'sidebar') {
    return (
      <div className="flex flex-col h-full">
        <EditorialHeader title={title} description={description} actions={actions} />
        <div className={cn('flex-1 p-4', contentClassName)}>
          {children}
        </div>
      </div>
    );
  }

  // Default variant with full page wrapper
  return (
    <div className="bg-paper-light h-full flex flex-col">
      <EditorialHeader title={title} description={description} actions={actions} />
      <div
        className={cn(
          'p-4 flex-1 flex flex-col min-h-0',
          maxWidth === '6xl' && 'container mx-auto max-w-6xl',
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  );
};
